export type Role =
  | 'developer'
  | 'candidate'
  | 'partner manager'
  | 'partner developer'
  | 'partner candidate';

export enum ProfileCompletionStatus {
  InitialProfileSubmissionPending = 'InitialProfileSubmissionPending',
  InitialExperienceSubmissionPending = 'InitialExperienceSubmissionPending',
  InitialFeedbackPending = 'InitialFeedbackPending',
  RepeatedFeedbackPending = 'RepeatedFeedbackPending',
  NoActionPending = 'NoActionPending',
}

export enum ContactStatus {
  NEW = 'New',
  IN_PROCESS = 'In Process',
  SCREENING_CALL = 'Screening call',
  HARD_SKILLS_INTERVIEW = 'Hard skills interview',
  FINAL_BRIEFING = 'Final briefing',
  AVAILABLE = 'Available',
  TEMPORARY_UNAVAILABLE = 'Temporary unavailable',
  PARTNERS_LISTED = 'Partners listed',
  DISABLED = 'Disabled',
  QUIT = 'Quit',
  FIRED = 'Fired',
  FULLY_ENGAGED = 'Fully engaged with client',
  LONG_LIST = 'Long list',
  REFUSED = 'Refused',
  FOR_FUTURE = 'For future',
  DECLINED = 'Declined',
  UNSUPPORTED_COUNTRY = 'Unsupported country',
  FILTERED_OUT = 'FilteredOut',
  OUTREACHED = 'Outreached',
  DELETE = 'Delete',
  ENGAGED = 'Engaged',
}

export enum TeamStatus {
  New = 'New',
  InProgress = 'InProgress',
  Active = 'Active',
  FilteredOut = 'FilteredOut',
  OnProbation = 'OnProbation',
  Delete = 'Delete',
}

export interface Team {
  status: TeamStatus;
}

export interface Skill {
  id: string;
  name: string;
  years: string | null;
  main: boolean;
}

export interface Specialization {
  id?: string;
  title: string;
  seniority?: string | null;
  show_in_cv?: boolean;
  has_custom_title?: boolean;
}

export enum AvailabilityType {
  FullTimeOnly = 'FullTimeOnly',
  PartTimeOnly = 'PartTimeOnly',
  BothPartAndFullTime = 'BothPartAndFullTime',
  PartTimeCanSwitchToFullTime = 'PartTimeCanSwitchToFullTime',
  Unavailable = 'Unavailable',
}

export enum FullTimeConfirmation {
  Accepted = 'Accepted',
  Declined = 'Declined',
  TalkToRecruiter = 'TalkToRecruiter',
}

export enum EnglishLevel {
  GoogleTranslates = 'google translates for me',
  OnlyWritten = 'only written communication',
  SimpleConversation = 'can hold simple conversations',
  CanComplexTasks = 'can discuss complex tasks',
}

export enum JobSearchStatus {
  ActivelyLooking = 'ActivelyLooking',
  OpenToOffers = 'OpenToOffers',
  NotLookingButCurious = 'NotLookingButCurious',
}

export type AvailabilityHours =
  | '<20h'
  | '20h'
  | '25h'
  | '30h'
  | '35h'
  | '40h'
  | 'Unknown';

export interface Availability {
  available_hours_per_week?: AvailabilityHours;
  availability_type: AvailabilityType;
  potentiallyAvailableSince?: string;
}

export enum ApplicationStage {
  QualificationStarted = 'QualificationStarted',
  ResumeConfirmed = 'ResumeConfirmed',
  RoleConfirmed = 'RoleConfirmed',
  AiCallStarted = 'AiCallStarted',
  AiCallCompleted = 'AiCallCompleted',
  ManualFormStarted = 'ManualFormStarted',
  ManualFormCompleted = 'ManualFormCompleted',
  TechPreScreeningStarted = 'TechPreScreeningStarted',
  TechPreScreeningCompleted = 'TechPreScreeningCompleted',
  ApplicationStarted = 'ApplicationStarted',
  ApplicationReopened = 'ApplicationReopened',
  ApplicationReviewStarted = 'ApplicationReviewStarted',
  ApplicationSetOnHold = 'ApplicationSetOnHold',
  ProfileSubmittedForReview = 'ProfileSubmittedForReview',
  ScreeningCallInvitationSent = 'ScreeningCallInvitationSent',
  ScreeningCallBooked = 'ScreeningCallBooked',
  ScreeningCallMade = 'ScreeningCallMade',
  HardSkillsInvitationSent = 'HardSkillsInvitationSent',
  HardSkillsInterviewBooked = 'HardSkillsInterviewBooked',
  HardSkillsInterviewMade = 'HardSkillsInterviewMade',
  FinalBriefingStarted = 'FinalBriefingStarted',
  ExperienceSubmittedForReview = 'ExperienceSubmittedForReview',
  OnboardedToCommunity = 'OnboardedToCommunity',
  ApplicationPaused = 'ApplicationPaused',

  // VideoAsk is deprecated
  VideoAskStarted = 'VideoAskStarted',
  VideoAskCompleted = 'VideoAskCompleted',
}

export interface Profile extends Availability {
  full_time_confirmation?: FullTimeConfirmation;
  job_search_status?: JobSearchStatus | null;
  id: string;
  firstName: string;
  lastName: string;
  lemonId: string | null;
  profile_photo: string;
  main_specialization: string;
  email: string;
  link_to_cv: string;
  experience_since: string | null;
  phone: string;
  telegram: string;
  codesamples_link: string;
  introduction: string | null;
  professional_intro: string | null;
  apiKey: string;
  linkedin: string | null;
  max_rate_per_hour?: number | null;
  min_rate_per_hour?: number | null;
  role: Role;
  cv_url?: string;
  cv_filename?: string;
  profile_completion_status: ProfileCompletionStatus;
  status_in_system: ContactStatus;
  applicationStage: ApplicationStage;
  teamId?: string | null;
  team?: Team;
  check?: { score?: number | null };
  operatorEmail?: boolean | string;
  chatLink?: string;
  country?: string;
  en_spoken?: EnglishLevel | null;
  isInitialProfileReviewPending?: boolean;
  isInitialExperienceReviewPending?: boolean;
  isInitialProfileReviewCompleted?: boolean;
  isInitialExperienceReviewCompleted?: boolean;
  seniority?: string;
  readyToStartWhen?: string;
  vacationPlans?: string;
  timeSlotsForInterview?: string;
  timeZone?: string | null;
  workingHoursFrom?: string | null;
  workingHoursTo?: string | null;
  subscriptionsCount?: number;
}

export enum Step {
  AUTO_CHECK = 'AUTO_CHECK',
  CALL_WITH_MARK = 'CALL_WITH_MARK',
  TECH_CHECK = 'TECH_CHECK',
  COMPLETE_PROFILE = 'COMPLETE_PROFILE',
  SCREENING_CALL = 'SCREENING_CALL',
  TECH_REVIEW = 'TECH_REVIEW',
  GET_YOUR_FIRST_PROJECT = 'GET_YOUR_FIRST_PROJECT',
}

export enum StepStatus {
  Active = 'active',
  Processing = 'processing',
  Verifying = 'verifying',
  Completed = 'completed',
  Disabled = 'disabled',
}

export interface ApplicationState {
  currentStep: Step;
  currentStatus: StepStatus;
  isApplicationStopped: boolean;
  stageComment?: string;
  stepAdditionalInfo: {
    bookedCallTimes?: string[] | null;
  };

  skippedSteps?: {
    callWithMark?: boolean;
    techCheck?: boolean;
  };
  hideClarificationSteps?: boolean;
  profileReviewSubmitted?: boolean;
}

export interface AdditionalLanguage {
  id: string;
  language: string;
  level: string;
}
